import { IconButton, TextField } from "@mui/material";
import { Form, Formik, FormikState } from "formik";
import { IMeetingTypeValues, IMeetingItem } from "../../RCAMeetingTypesSettingInterfaces";
import { meetingTypesFormValidationSchema } from "validations/schemas";
import { SetStateAction, useMemo } from "react";
import useRolesPermissions from "hooks/useRolesPermissions";
import { useStyles } from "../../RCAMeetingSetting.styles";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

export const MeetingTextfieldForm = ({
    initFormValues,
    onSubmit,
    onKeyDown,
    setTypeToEdit,
    placeholder,
}: {
    initFormValues: IMeetingTypeValues;
    onSubmit: (
        values: IMeetingTypeValues,
        helpers?: {
            resetForm?: (nextState?: Partial<FormikState<IMeetingTypeValues>> | undefined) => void;
            setSubmitting?: (isSubmitting: boolean) => void;
        },
    ) => Promise<void>;
    onKeyDown?: boolean;
    setTypeToEdit?: (value: SetStateAction<IMeetingItem | null>) => void;
    placeholder?: string;
}) => {
    const { RCA_PERMISSIONS } = useRolesPermissions();
    const classes = useStyles();
    const { t } = useTranslation();
    const userCanEdit = useMemo(() => RCA_PERMISSIONS?.settings?.canEdit, [RCA_PERMISSIONS]);

    return (
        <Formik initialValues={initFormValues} validationSchema={meetingTypesFormValidationSchema} onSubmit={onSubmit}>
            {({ isSubmitting, errors, touched, values, handleChange, resetForm, setTouched, setSubmitting }) => {
                return (
                    <Form style={{ ...(touched.name && errors.name && { marginBottom: "16px" }) }}>
                        <TextField
                            autoFocus={true}
                            disabled={!userCanEdit}
                            fullWidth
                            id="meeting-type-name-input"
                            name="name"
                            type="text"
                            variant="outlined"
                            placeholder={placeholder}
                            className={classes.textField}
                            label={t("rca.settings.meeting_settings.textfield_label")}
                            value={values.name}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name && t(errors.name)}
                            onBlur={(event) => {
                                setTouched({ [event.target.name]: false });
                            }}
                            onKeyDown={
                                onKeyDown
                                    ? (event) => {
                                          if (event.key === "Enter") {
                                              event.preventDefault();
                                              if (Boolean(values.name.trim()) && !isSubmitting) {
                                                  onSubmit(values, { resetForm, setSubmitting });
                                              }
                                          }
                                      }
                                    : undefined
                            }
                            InputProps={{
                                endAdornment: setTypeToEdit ? (
                                    <>
                                        <IconButton disabled={isSubmitting || !userCanEdit} type="submit">
                                            <CheckIcon />
                                        </IconButton>
                                        <IconButton disabled={isSubmitting} onClick={() => setTypeToEdit(null)}>
                                            <CloseIcon />
                                        </IconButton>
                                    </>
                                ) : (
                                    <IconButton disabled={isSubmitting || !userCanEdit} type="submit">
                                        <AddIcon />
                                    </IconButton>
                                ),
                            }}
                            onChange={handleChange}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};
